import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, Trash2, ShoppingBag, Heart, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useStore } from "@/context/StoreContext";
import { inr, offerNameLabel, FALLBACK_IMG } from "@/lib/api";
import FreeDeliveryNudge from "@/components/FreeDeliveryNudge";

export default function ShopDrawer() {
  const {
    drawer, closeDrawer, setDrawerTab, isAuthed, setLoginOpen,
    cartData, updateQty, removeCartItem, applyCoupon, removeCoupon,
    wishlist, toggleWishlist, addToCart,
  } = useStore();
  const [code, setCode] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (drawer.open) window.__lenis?.stop();
    else window.__lenis?.start();
  }, [drawer.open]);

  const items = cartData?.cart?.items || [];
  const totals = cartData?.totals;

  // offer_discount is a subset of product_discount (both are off MRP) — never sum them
  const savings = (totals?.product_discount || 0) + (totals?.coupon_discount || 0);

  const goCheckout = () => {
    closeDrawer();
    navigate("/checkout");
  };

  return (
    <AnimatePresence>
      {drawer.open && (
        <>
          <motion.div data-testid="drawer-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={closeDrawer} className="fixed inset-0 z-[80] bg-cream/40 backdrop-blur-sm" />
          <motion.aside
            data-testid="shop-drawer"
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 260, damping: 30 }}
            className="fixed right-0 top-0 z-[90] flex h-full w-full max-w-md flex-col border-l border-champagne/40 bg-pine"
          >
            <div className="flex items-center justify-between border-b border-champagne/30 p-5">
              <div className="flex gap-2">
                {[{ key: "bag", label: "Shopping Bag", icon: ShoppingBag }, { key: "saved", label: "Saved", icon: Heart }].map((t) => (
                  <button key={t.key} data-testid={`drawer-tab-${t.key}`} onClick={() => setDrawerTab(t.key)} className={`relative flex items-center gap-2 rounded-full px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors ${drawer.tab === t.key ? "text-forest" : "text-sage hover:text-cream"}`}>
                    {drawer.tab === t.key && <motion.span layoutId="drawer-tab-pill" className="absolute inset-0 rounded-full bg-gold" transition={{ type: "spring", stiffness: 300, damping: 28 }} />}
                    <t.icon className="relative z-10 h-3.5 w-3.5" />
                    <span className="relative z-10">{t.label}</span>
                  </button>
                ))}
              </div>
              <button data-testid="drawer-close-button" onClick={closeDrawer} className="flex h-9 w-9 items-center justify-center rounded-full border border-champagne/40 text-cream hover:border-gold hover:text-gold" aria-label="Close drawer">
                <X className="h-4 w-4" />
              </button>
            </div>

            {!isAuthed ? (
              <div className="flex flex-1 flex-col items-center justify-center p-6 text-center" data-testid="drawer-login-prompt">
                <ShoppingBag className="h-10 w-10 text-gold" />
                <p className="mt-4 font-display text-xl text-cream">Sign in to open your bag</p>
                <p className="mt-2 max-w-[240px] text-xs text-fern">Your bag and saved rituals sync to your mobile number.</p>
                <button data-testid="drawer-login-btn" onClick={() => { closeDrawer(); setLoginOpen(true); }} className="mt-6 rounded-full bg-gold px-8 py-3.5 text-xs font-bold uppercase tracking-[0.2em] text-forest">Sign in with OTP</button>
              </div>
            ) : drawer.tab === "bag" ? (
              <>
                <div className="flex-1 overflow-y-auto p-5">
                  {items.length === 0 ? (
                    <div className="flex h-full flex-col items-center justify-center text-center" data-testid="cart-empty-state">
                      <ShoppingBag className="h-10 w-10 text-fern" />
                      <p className="mt-4 font-display text-xl text-cream">Your bag is empty</p>
                      <p className="mt-2 max-w-[220px] text-xs text-fern">Begin your ritual — add something beautiful from the Price Edit.</p>
                    </div>
                  ) : (
                    <>
                      {savings > 0 && (
                        <motion.div
                          initial={{ opacity: 0, y: -8 }}
                          animate={{ opacity: 1, y: 0 }}
                          data-testid="bag-savings-banner"
                          className="mb-5 flex items-center gap-2.5 rounded-2xl border border-gold/40 bg-gold/15 px-4 py-3"
                        >
                          <Sparkles className="h-4 w-4 shrink-0 text-gold" />
                          <p className="text-xs font-bold text-gold">You're saving {inr(savings)} on this order</p>
                        </motion.div>
                      )}
                      <ul className="space-y-5">
                      {items.map((it) => (
                        <li key={it.id} data-testid={`cart-item-${it.id}`} className="flex gap-4">
                          <div className="h-20 w-16 shrink-0 overflow-hidden rounded-xl border border-champagne/40 bg-moss">
                            <img src={it.image || it.primary_image || it.thumbnail || FALLBACK_IMG} onError={(e) => { if (!e.currentTarget.dataset.fbk) { e.currentTarget.dataset.fbk = "1"; e.currentTarget.src = FALLBACK_IMG; } }} alt={it.name} className="h-full w-full object-cover" />
                          </div>
                          <div className="flex flex-1 flex-col">
                            <p className="line-clamp-1 font-display text-sm text-cream">{it.name}</p>
                            {it.offer_name && (
                              <p data-testid={`drawer-offer-${it.id}`} className="mt-0.5 inline-flex w-fit items-center rounded-full bg-black px-2.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-widest text-white">Offer · {offerNameLabel(it.offer_name)}</p>
                            )}
                            <div className="mt-auto flex items-center justify-between">
                              <div className="flex items-center gap-3 rounded-full border border-champagne/40 px-2 py-1">
                                <button data-testid={`qty-dec-${it.id}`} onClick={() => updateQty(it.id, it.qty - 1)} className="text-sage hover:text-gold" aria-label="Decrease quantity"><Minus className="h-3 w-3" /></button>
                                <span data-testid={`qty-value-${it.id}`} className="w-4 text-center text-xs font-bold text-cream">{it.qty}</span>
                                <button data-testid={`qty-inc-${it.id}`} onClick={() => updateQty(it.id, it.qty + 1)} className="text-sage hover:text-gold" aria-label="Increase quantity"><Plus className="h-3 w-3" /></button>
                              </div>
                              <div className="text-right">
                                <span className="text-sm font-bold text-cream">{inr(it.line_total)}</span>
                                {it.list_price > it.selling_price && (
                                  <p className="font-mono text-[10px] text-fern line-through">{inr(it.list_price * it.qty)}</p>
                                )}
                              </div>
                            </div>
                          </div>
                          <button data-testid={`remove-item-${it.id}`} onClick={() => removeCartItem(it.id)} className="self-start text-fern hover:text-gold" aria-label="Remove item">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </li>
                      ))}
                      </ul>
                    </>
                  )}
                </div>

                {items.length > 0 && (
                  <div className="border-t border-champagne/30 p-5">
                    <div className="mb-4">
                      <FreeDeliveryNudge totals={totals} items={items} />
                    </div>
                    {totals?.coupon_code ? (
                      <div className="flex items-center justify-between rounded-xl bg-moss px-4 py-2.5">
                        <span className="font-mono text-xs text-cream">{totals.coupon_code}</span>
                        <button data-testid="drawer-coupon-remove" onClick={removeCoupon} className="font-mono text-[10px] uppercase tracking-widest text-gold hover:underline">Remove</button>
                      </div>
                    ) : !totals?.has_product_offers && (
                      <div className="flex gap-2">
                        <input data-testid="promo-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Coupon code" className="flex-1 rounded-full border border-champagne/50 bg-moss px-4 py-2.5 text-xs text-cream placeholder:text-fern focus:border-gold focus:outline-none" />
                        <button data-testid="promo-apply-button" onClick={() => code.trim() && applyCoupon(code.trim().toUpperCase())} className="rounded-full border border-gold/50 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] text-gold transition-colors hover:bg-gold hover:text-forest">Apply</button>
                      </div>
                    )}
                    <div className="mt-4 space-y-1.5 text-sm">
                      <div className="flex justify-between text-sage"><span>MRP total</span><span>{inr(totals?.subtotal_mrp)}</span></div>
                      {(totals?.product_discount || 0) - (totals?.offer_discount || 0) > 0 && (
                        <div className="flex justify-between text-gold"><span>Product savings</span><span>-{inr((totals?.product_discount || 0) - (totals?.offer_discount || 0))}</span></div>
                      )}
                      {totals?.offer_discount > 0 && <div className="flex justify-between font-bold text-gold" data-testid="drawer-offer-savings"><span>Offer savings</span><span>-{inr(totals.offer_discount)}</span></div>}
                      {totals?.coupon_discount > 0 && <div className="flex justify-between text-gold" data-testid="discount-line"><span>Coupon</span><span>-{inr(totals.coupon_discount)}</span></div>}
                      <div className="flex justify-between text-sage"><span>Shipping</span><span className="font-mono text-[10px] uppercase tracking-widest text-fern">At checkout · by pincode</span></div>
                      <div className="flex justify-between border-t border-champagne/30 pt-2 text-base font-bold text-cream">
                        <span>Payable</span><span data-testid="total-value">{inr(totals?.payable)}</span>
                      </div>
                    </div>
                    <button data-testid="checkout-button" onClick={goCheckout} className="mt-4 w-full rounded-full bg-gold py-4 text-xs font-bold uppercase tracking-[0.2em] text-forest transition-transform hover:scale-[1.02]">
                      Checkout — {inr(totals?.payable)}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="flex-1 overflow-y-auto p-5">
                {wishlist.products.length === 0 ? (
                  <div className="flex h-full flex-col items-center justify-center text-center" data-testid="saved-empty-state">
                    <Heart className="h-10 w-10 text-fern" />
                    <p className="mt-4 font-display text-xl text-cream">No saved rituals yet</p>
                    <p className="mt-2 max-w-[220px] text-xs text-fern">Tap the heart on any product to keep it here.</p>
                  </div>
                ) : (
                  <ul className="space-y-5">
                    {wishlist.products.map((p) => (
                      <li key={p.product_id} data-testid={`saved-item-${p.product_id}`} className="flex items-center gap-4">
                        <div className="h-20 w-16 shrink-0 overflow-hidden rounded-xl border border-champagne/40 bg-moss">
                          <img src={p.image || FALLBACK_IMG} onError={(e) => { if (!e.currentTarget.dataset.fbk) { e.currentTarget.dataset.fbk = "1"; e.currentTarget.src = FALLBACK_IMG; } }} alt={p.name} className="h-full w-full object-cover" />
                        </div>
                        <div className="flex-1">
                          <p className="line-clamp-1 font-display text-sm text-cream">{p.name}</p>
                          <p className="mt-1 text-sm font-bold text-cream">
                            {p.offer_price < p.selling_price && <span className="mr-2 text-xs font-normal text-fern line-through">{inr(p.selling_price)}</span>}
                            {inr(p.offer_price ?? p.selling_price)}
                          </p>
                          {!p.in_stock && <p className="font-mono text-[9px] uppercase tracking-widest text-gold">Out of stock</p>}
                        </div>
                        <button data-testid={`saved-add-${p.product_id}`} onClick={() => addToCart(p.product_id, 1)} disabled={!p.in_stock} className="rounded-full bg-cream px-4 py-2 font-mono text-[9px] font-bold uppercase tracking-[0.15em] text-forest hover:bg-gold disabled:opacity-50">
                          Add
                        </button>
                        <button data-testid={`saved-remove-${p.product_id}`} onClick={() => toggleWishlist(p.product_id)} className="text-fern hover:text-gold" aria-label="Remove from saved">
                          <X className="h-4 w-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
